import { type ObjectType } from "store/appData";
import {
  type DatasetImage,
  type DatasetKind,
  TRUE_POSITIVE,
  SUPERVISOR_APPROVED,
  FALSE_POSITIVE,
  SUPER_FALSE_POSITIVE,
} from "../api";

// Statuses the unsuperverify endpoint reverts (to True Positive / False
// Positive respectively) — see apps/global-api's unsuperverify_image.
export const SUPERVISOR_VERIFIED_STATUSES = new Set([
  SUPERVISOR_APPROVED,
  SUPER_FALSE_POSITIVE,
]);

// True when the image carries a detection with one of `statuses`. In defect
// datasets, pairs on non-issue types (e.g. the "object has no defect"
// pseudo-type) don't count — mirrors DATASET_API.defect.hasPositive in
// ../api.ts.
function imageHasStatus(
  image: DatasetImage,
  kind: DatasetKind,
  objectTypes: Record<number, ObjectType>,
  statuses: ReadonlySet<number>,
): boolean {
  return image.type_x_workflow_status.some(
    ([typeId, status]) =>
      statuses.has(status) &&
      (kind !== "defect" || objectTypes[typeId]?.issue === true),
  );
}

const TRUE_POSITIVE_STATUSES = new Set([TRUE_POSITIVE, SUPERVISOR_APPROVED]);
const FALSE_POSITIVE_STATUSES = new Set([
  FALSE_POSITIVE,
  SUPER_FALSE_POSITIVE,
]);

// True when the image carries at least one True Positive or Supervisor
// Approved detection.
export function imageHasTruePositive(
  image: DatasetImage,
  kind: DatasetKind,
  objectTypes: Record<number, ObjectType>,
): boolean {
  return imageHasStatus(image, kind, objectTypes, TRUE_POSITIVE_STATUSES);
}

// True when the image carries at least one False Positive or Supervisor
// False Positive detection, independent of whether it also has a true
// positive.
export function imageHasFalsePositive(
  image: DatasetImage,
  kind: DatasetKind,
  objectTypes: Record<number, ObjectType>,
): boolean {
  return imageHasStatus(image, kind, objectTypes, FALSE_POSITIVE_STATUSES);
}
